import { getVariantDisplayQuantity } from '../../utils/variant-display';
import { Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import { getStorageObjectUrl, supabase } from '../../core/supabase/supabase.client';

export type OrderStatus =
  | 'PENDING'
  | 'CONFIRMED'
  | 'PREPARING'
  | 'READY_FOR_PICKUP'
  | 'RIDER_ASSIGNED'
  | 'RIDER_ACCEPTED'
  | 'RIDER_AT_STORE'
  | 'DISPATCHED'
  | 'RIDER_NEAR_CUSTOMER'
  | 'DELIVERED'
  | 'CANCELLED';
export type PaymentStatus = 'PENDING' | 'PAID' | 'FAILED' | 'REFUNDED';

export interface OrderItem {
  id: number;
  order_id: number;
  product_variant_id: number;
  price: number;
  quantity: number;
  total: number;
  variant?: {
    sku: string;
    product: {
      name: string;
    };
  };
}

export interface Order {
  id: number;
  user_id: number;
  store_id: string;
  rider_id: number | null;
  status: OrderStatus;
  payment_status: PaymentStatus;
  total_amount: number;
  payable_amount: number;
  delivery_fee?: number;
  small_order_fee?: number;
  original_delivery_fee?: number;
  delivery_discount?: number;
  created_at: string;
  user: {
    name: string;
    phone: string;
  };
  store: {
    store_name: string;
  };
  rider?: {
    name: string;
  };
  items?: OrderItem[];
}

export interface AdminOrderRider {
  id: string;
  rider_id: string;
  user_id?: string | null;
  store_id?: number | null;
  full_name?: string | null;
  name?: string;
  code?: string | null;
  vehicle_type?: string | null;
  status?: string | null;
  verification_status?: string | null;
  onboarding_step?: string | null;
  review_remark?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  profile_photo_url?: string | null;
  profile_photo?: { bucket: string; path: string } | null;
  account?: {
    id?: string;
    name?: string | null;
    phone?: string | null;
    email?: string | null;
    is_active?: boolean | null;
  } | null;
  presence?: {
    is_online?: boolean | null;
    status?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    last_seen_at?: string | null;
    updated_at?: string | null;
  } | null;
}

export interface CanonicalBillSummary {
  order_id: number;
  merchandise: {
    original: number;
    removed: number;
    fulfilled: number;
  };
  fees: {
    delivery: {
      charged: number;
      original: number;
      waiver: number;
      is_free: boolean;
    };
    packaging: number;
    platform: number;
    small_order: number;
  };
  discounts: {
    order_discount: number;
    coins: number;
  };
  savings: {
    mrp: number | null;
    mrp_reliable: boolean;
  };
  payment: {
    method: string;
    status: string;
  };
  current_payable: number;
}

export interface AdminOrderItemViewModel {
  id: number;
  sku: string;
  variant_sku?: string;
  product_name: string;
  variant_name: string;
  image_url: string | null;
  image?: string | null;
  unit: string;
  
  is_fully_removed: boolean;
  is_partially_removed: boolean;

  ordered_quantity: number;
  removed_quantity: number;
  fulfilled_quantity: number;
  
  historical_unit_price: number;
  original_line_total: number;
  removed_line_total: number;
  fulfilled_line_total: number;
  
  // Legacy / fallback mappings
  order_quantity?: number;
  cancelled_quantity?: number;
  product_variant_id?: number;
  price?: number;
  mrp?: number;
  total?: number;
  item_savings?: number;
  variant_quantity?: number;
}

export interface AdminOrder {
  order_id: number;
  order_number: string | null;
  status: string;
  current_status: OrderStatus;
  payment_status: string;
  payable_amount: number;
  total_amount: number;
  canonical_bill?: CanonicalBillSummary;
  pricing?: {
    subtotal?: number;
    coin_used?: number;
    delivery_fee?: number;
    platform_fee?: number;
    packaging_fee?: number;
    payable_amount?: number;
    discount_amount?: number;
    small_order_fee?: number;
    delivery_discount?: number;
    original_delivery_fee?: number;
  };
  created_at: string;
  updated_at: string;
  delivery_address: string;
  delivery_phone: string;
  delivery?: {
    lat?: number;
    lng?: number;
    phone?: string;
    address?: {
      receiver_name?: string;
      phone_number?: string;
      [key: string]: any;
    };
  };
  store?: {
    name?: string;
    store_id?: number;
    store_name?: string;
    latitude?: number;
    longitude?: number;
    lat?: number;
    lng?: number;
  };
  rider?: AdminOrderRider | null;
  promotions?: {
    name?: string;
    type?: string;
    promotion_id?: string;
    discount_amount?: number;
  }[];
  rider_id: string | null;
  assigned_at: string | null;
  delivered_at: string | null;
  payment_method?: string | null;
  payment_reference?: string | null;

  order_source?: string | null;
  created_by_admin?: string | null;
  assisted_order_reason?: string | null;
  assisted_order_note?: string | null;
  idempotency_key?: string | null;

  // Picking Slip / Fulfillment Tracking
  picking_slip_print_triggered_at?: string | null;
  picking_slip_trigger_count?: number;
  picking_slip_claimed_at?: string | null;
  fulfillment_revision?: number;
  picking_slip_printed_revision?: number | null;

  // Actionable Metadata
  priority: 'HIGH' | 'MEDIUM' | 'LOW';
  action_required: string;
  address_status: string;
  phone_status: string;

  customer: {
    user_id?: number;
    name?: string;
    receiver_name?: string;
    phone: string;
    phone_status: string;
    address_status: string;
    delivery_address: Record<string, unknown>;
  };
  summary: {
    items: number;
    quantity: number;
    total_amount: number;
    payment_status: string;
    item_count: number;
    total_items: number;
  };
  items: AdminOrderItemViewModel[];
  timeline: {
    status: string;
    time: string;
  }[];
  status_history: {
    status: string;
    actor: string;
    note: string;
    created_at: string;
  }[];
  deliveries?: {
    id?: number;
    status?: string;
    rider_id?: string;
    assigned_at?: string;
    picked_at?: string;
    delivered_at?: string;
    estimated_distance_km?: number;
    estimated_delivery_minutes?: number;
    estimated_payout?: number;
    final_payout?: number;
  }[];
}

export type AdminOrderLookupType =
  | 'order_id'
  | 'order_number'
  | 'rider_id'
  | 'rider_user_id'
  | 'customer_user_id'
  | 'empty'
  | 'not_found';

export interface AdminOrderLookupResult {
  orders: AdminOrder[];
  lookupType: AdminOrderLookupType;
  message?: string;
}

export interface AdminOrdersPage {
  data: AdminOrder[];
  total: number;
  hasMore: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class OrdersService {
  private table = 'orders';
  private adminOrderCache = new Map<number, AdminOrder>();

  private readonly adminOrderDetailSelect = `
        id,
        order_number,
        status,
        payment_status,
        payment_method,
        payment_reference,
        fulfillment_revision,
        payable_amount,
        total_amount,
        discount_amount,
        coin_used,
        delivery_fee,
        packaging_fee,
        platform_fee,
        small_order_fee,
        delivery_discount,
        original_delivery_fee,
        picking_slip_print_triggered_at,
        picking_slip_trigger_count,
        picking_slip_claimed_at,
        order_source,
        created_by_admin,
        assisted_order_reason,
        assisted_order_note,
        created_at,
        updated_at,
        assigned_at,
        delivered_at,
        rider_id,
        user_id,
        store_id,
        delivery_address,
        delivery_lat,
        delivery_lng,
        delivery_phone,
        store_name,
        store_address,
        customer:users!orders_user_id_fkey(id, phone, name),
        store:stores(id, name, latitude, longitude),
        rider:riders!orders_rider_id_fkey(
          id,
          full_name,
          code,
          user_id,
          store_id,
          vehicle_type,
          status,
          verification_status,
          onboarding_step,
          review_remark,
          created_at,
          updated_at,
          account:users!riders_user_id_fkey(
            id,
            name,
            phone,
            email,
            is_active
          ),
          presence:rider_presence!rider_presence_rider_id_fkey(
            is_online,
            status,
            latitude,
            longitude,
            last_seen_at,
            updated_at
          ),
          profile_photo:files!riders_profile_photo_file_id_fkey(
            bucket,
            path
          )
        ),
        items:order_items!order_items_order_id_fkey(
          id,
          quantity,
          cancelled_quantity,
          price,
          total,
          product_variant_id,
          variant:product_variants(
            product_id,
            net_quantity,
            sku,
            unit,
            quantity,
            mrp,
            product:products(
              id,
              name,
              description,
              images:product_images(
                is_primary,
                sort_order,
                files:files!product_images_file_id_fkey(bucket, path)
              )
            )
          )
        ),
        promotions:order_promotions(
          discount_amount,
          promotion:promotions(id, name, type)
        ),
        deliveries:deliveries(
          id,
          status,
          rider_id,
          assigned_at,
          picked_at,
          delivered_at,
          estimated_distance_km,
          estimated_delivery_minutes,
          estimated_payout,
          final_payout
        ),
        status_history:order_status_history(*)
      `;

  getOrders(
    page = 1,
    limit = 20,
    status?: OrderStatus,
    search?: string
  ): Observable<{ data: Order[]; total: number }> {
    let query = supabase.from(this.table).select(
      `
        *,
        user:users!orders_user_id_fkey (name, phone),
        store:stores (name),
        rider:riders!orders_rider_id_fkey (id, full_name, code)
      `,
      { count: 'exact' }
    );

    if (status) {
      query = query.eq('status', status);
    }

    if (search) {
      // Basic search on user name or phone or order ID if numeric
      if (!isNaN(Number(search))) {
        query = query.or(`id.eq.${search},user_id.eq.${search}`);
      } else {
        // PostgREST doesn't support easy joining for filters on nested fields directly in .or()
        // but we can try filtering by user name if the schema allows text search or use a RPC
        // For now, let's keep it simple or use partial match on status if needed
      }
    }

    const from_row = (page - 1) * limit;
    const to_row = from_row + limit - 1;

    query = query
      .order('created_at', { ascending: false })
      .range(from_row, to_row);

    return from(
      query.then(({ data, count, error }) => {
        if (error) throw error;
        return {
          data: data as unknown as Order[],
          total: count || 0,
        };
      })
    );
  }

  subscribeToOrders(
    callback: (payload: any) => void,
    storeId?: string | number | null
  ) {
    const filter = storeId ? `store_id=eq.${storeId}` : undefined;
    return supabase
      .channel(`admin-orders:${storeId ?? 'all'}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders',
          ...(filter ? { filter } : {}),
        },
        callback
      )
      .subscribe();
  }

  getOrderDetails(id: number): Observable<Order> {
    return from(
      supabase
        .from(this.table)
        .select(
          `
        *,
        user:users!orders_user_id_fkey (name, phone),
        store:stores (name),
        rider:riders!orders_rider_id_fkey (id, full_name, code),
        items:order_items (
          *,
          variant:product_variants (
            sku,
            net_quantity,
            quantity,
            unit,
            product:products (name)
          )
        )
      `
        )
        .eq('id', id)
        .single()
        .then(({ data, error }) => {
          if (error) throw error;
          return data as unknown as Order;
        })
    );
  }

  updateOrderStatus(id: number, status: OrderStatus): Observable<void> {
    if (status === 'DELIVERED') {
      return this.adminCompleteOrder(id);
    }

    return from(
      supabase
        .rpc('change_order_status', {
          p_order_id: id,
          p_new_status: status.toLowerCase(),
          p_actor: 'admin',
          p_note: `Admin changed order status to ${status.toLowerCase()}`,
        })
        .then(({ error }) => {
          if (error) throw error;
          const cachedOrder = this.adminOrderCache.get(id);
          if (cachedOrder) {
            this.adminOrderCache.set(id, {
              ...cachedOrder,
              status: status.toLowerCase(),
              current_status: status,
              priority: this.getOrderPriority(status),
              action_required: this.getOrderAction(status),
            });
          }
        })
    );
  }

  /** Transactional completion; backend owns payment, stock and rider settlement. */
  adminCompleteOrder(
    orderId: number,
    options?: { note?: string; requestId?: string; mode?: 'STORE_COLLECTION' | 'RIDER_DELIVERY'; paymentAction?: 'EXISTING_PAYMENT' | 'CASH_COLLECTED'; receipt?: { revision: number; items: { item_id: number; quantity: number }[]; handoverConfirmed: boolean } }
  ): Observable<void> {
    return from(
      supabase
        .rpc(options?.receipt ? 'admin_complete_order_v3' : 'admin_complete_order_v2', {
          p_request_id: options?.requestId ?? crypto.randomUUID(),
          p_order_id: Number(orderId),
          p_mode: options?.mode ?? 'RIDER_DELIVERY',
          p_payment_action: options?.paymentAction ?? 'EXISTING_PAYMENT',
          p_reason:
            options?.note ??
            'Order completed by admin (payment status unchanged)',
          ...(options?.receipt ? {
            p_revision: options.receipt.revision,
            p_items: options.receipt.items,
            p_handover_confirmed: options.receipt.handoverConfirmed,
          } : {}),
        })
        .then(({ data, error }) => {
          if (error) throw error;
          const payload = data as Record<string, unknown> | null;
          if (payload && payload['success'] === false) {
            throw new Error(String(payload['error'] ?? 'Complete failed'));
          }

          const cachedOrder = this.adminOrderCache.get(orderId);
          if (cachedOrder) {
            this.adminOrderCache.set(orderId, {
              ...cachedOrder,
              status: 'delivered',
              current_status: 'DELIVERED',
              payment_status: 'PAID',
              priority: this.getOrderPriority('DELIVERED'),
              action_required: this.getOrderAction('DELIVERED'),
            });
          }
        })
    );
  }

  /** Pack complete → ready_for_pickup → atomic nearest-rider auto-assign */
  markOrderReadyForPickup(
    orderId: number,
    options?: { distanceKm?: number; estimatedMinutes?: number }
  ): Observable<void> {
    return from(
      supabase
        .rpc('mark_order_ready_for_pickup', {
          p_order_id: Number(orderId),
          p_actor: 'admin',
          p_note: 'Order marked ready for pickup by admin',
          p_distance_km: options?.distanceKm ?? 2,
          p_estimated_minutes: Math.max(
            1,
            Math.round(options?.estimatedMinutes ?? 15)
          ),
        })
        .then(({ data, error }) => {
          if (error) throw error;
          const payload = data as Record<string, unknown> | null;
          const assignment = payload?.['assignment'] as
            | Record<string, unknown>
            | undefined;
          if (assignment && assignment['success'] === false) {
            throw new Error(
              String(assignment['error'] ?? 'No available rider found')
            );
          }
        })
    );
  }
  confirmOrderFulfillment(orderId: number, confirmation: {
    revision: number; items: { item_id: number; quantity: number }[];
    stage: 'PACK' | 'HANDOVER'; riderId: string | null;
  }): Observable<void> {
    return from(supabase.rpc('admin_confirm_order_fulfillment', {
      p_order_id: orderId, p_revision: confirmation.revision,
      p_items: confirmation.items, p_stage: confirmation.stage,
      p_rider_id: confirmation.riderId, p_confirmed: true,
    }).then(({ error }) => { if (error) throw error; }));
  }
  public getDistanceAndDuration(
    origin: { lat: number; lng: number },
    destination: { lat: number; lng: number }
  ): Observable<{ distanceMeters: number; durationSeconds: number }> {
    return from(
      supabase.functions
        .invoke('google-distance', {
          body: {
            originLat: origin.lat,
            originLng: origin.lng,
            destinationLat: destination.lat,
            destinationLng: destination.lng,
          },
        })
        .then(({ data, error }) => {
          if (error) {
            throw error;
          }

          if (!data?.success) {
            throw new Error(data?.error || data?.error_message || 'Failed to calculate distance');
          }

          // Preferred: top-level fields from current google-distance function
          let distanceMeters = Number(data?.distance_meters ?? 0);
          let durationSeconds = Number(data?.duration_seconds ?? 0);

          // Fallback: Distance Matrix rows shape
          if (!(distanceMeters > 0 && durationSeconds > 0)) {
            const element =
              data?.rows?.[0]?.elements?.[0] ??
              data?.debug?.google_response?.rows?.[0]?.elements?.[0];

            if (element && String(element.status || 'OK') === 'OK') {
              distanceMeters = Number(element?.distance?.value ?? distanceMeters);
              durationSeconds = Number(element?.duration?.value ?? durationSeconds);
            }
          }

          if (!(distanceMeters > 0) || !(durationSeconds > 0)) {
            throw new Error(
              data?.element_status
                ? `Google distance element status: ${data.element_status}`
                : 'Invalid Google distance response'
            );
          }

          return {
            distanceMeters,
            durationSeconds,
          };
        })
    );
  }

  /** Resolve store → customer route points from admin order payloads. */
  resolveOrderRoutePoints(order: any): {
    origin: { lat: number; lng: number };
    destination: { lat: number; lng: number };
  } | null {
    const address =
      order?.delivery?.address && typeof order.delivery.address === 'object'
        ? order.delivery.address
        : order?.delivery_address && typeof order.delivery_address === 'object'
          ? order.delivery_address
          : order?.customer?.delivery_address &&
              typeof order.customer.delivery_address === 'object'
            ? order.customer.delivery_address
            : {};

    const originLat = Number(
      order?.store?.latitude ?? order?.store?.lat ?? order?.store_lat ?? 0
    );
    const originLng = Number(
      order?.store?.longitude ?? order?.store?.lng ?? order?.store_lng ?? 0
    );
    const destinationLat = Number(
      order?.delivery?.lat ??
        order?.delivery_lat ??
        address?.lat ??
        address?.latitude ??
        0
    );
    const destinationLng = Number(
      order?.delivery?.lng ??
        order?.delivery_lng ??
        address?.lng ??
        address?.longitude ??
        0
    );

    if (
      !Number.isFinite(originLat) ||
      !Number.isFinite(originLng) ||
      !Number.isFinite(destinationLat) ||
      !Number.isFinite(destinationLng) ||
      (originLat === 0 && originLng === 0) ||
      (destinationLat === 0 && destinationLng === 0)
    ) {
      return null;
    }

    return {
      origin: { lat: originLat, lng: originLng },
      destination: { lat: destinationLat, lng: destinationLng },
    };
  }

  /** Haversine fallback when Google Distance is unavailable. */
  estimateRouteFallback(
    origin: { lat: number; lng: number },
    destination: { lat: number; lng: number }
  ): { distanceKm: number; estimatedMinutes: number } {
    const toRad = (deg: number) => (deg * Math.PI) / 180;
    const earthKm = 6371;
    const dLat = toRad(destination.lat - origin.lat);
    const dLng = toRad(destination.lng - origin.lng);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(origin.lat)) *
        Math.cos(toRad(destination.lat)) *
        Math.sin(dLng / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distanceKm = Math.max(0.3, earthKm * c);
    // Rough urban two-wheeler speed ~22 km/h + 3 min buffer
    const estimatedMinutes = Math.max(5, Math.round((distanceKm / 22) * 60) + 3);
    return { distanceKm, estimatedMinutes };
  }
  assignRider(
    orderId: number,
    riderId: number | string,
    _status: OrderStatus = 'CONFIRMED',
    options?: {
      distanceKm?: number;
      estimatedMinutes?: number;
      note?: string;
    }
  ): Observable<void> {
    return this.assignRiderManually({
      orderId,
      riderId: String(riderId),
      distanceKm: options?.distanceKm,
      estimatedMinutes: options?.estimatedMinutes,
      note: options?.note,
    });
  }

  reassignRider(
    orderId: number | string,
    reason = 'Admin requested rider change'
  ): Observable<void> {
    return from(
      supabase
        .rpc('reassign_order_rider', {
          p_order_id: Number(orderId),
          p_reason: reason,
          p_actor: 'admin',
        })
        .then(({ error }) => {
          if (error) throw error;
        })
    );
  }

  adminCancelOrder(
    orderId: number | string,
    params: {
      reason: string;
      note?: string;
      disposition?: 'RESTOCK' | 'WASTAGE' | 'NO_STOCK_CHANGE';
    }
  ): Observable<void> {
    return from(
      supabase
        .rpc('admin_cancel_order', {
          p_order_id: Number(orderId),
          p_reason: params.reason,
          p_note: params.note || '',
          p_disposition: params.disposition || 'RESTOCK',
        })
        .then(({ data, error }) => {
          if (error) throw error;
          const payload = data as Record<string, unknown> | null;
          if (payload && payload['success'] === false) {
            throw new Error(String(payload['error'] || 'Cancellation failed'));
          }
        })
    );
  }

  adminReportDeliveryFailure(
    orderId: number | string,
    params: { reason: string; note?: string }
  ): Observable<void> {
    return from(
      supabase
        .rpc('admin_report_delivery_failure', {
          p_order_id: Number(orderId),
          p_reason: params.reason,
          p_note: params.note || '',
        })
        .then(({ data, error }) => {
          if (error) throw error;
          const payload = data as Record<string, unknown> | null;
          if (payload && payload['success'] === false) {
            throw new Error(String(payload['error'] || 'Delivery failure report failed'));
          }
        })
    );
  }

  adminConfirmReturnToStore(
    orderId: number | string,
    params: { disposition: 'RESTOCK' | 'WASTAGE' | 'NO_STOCK_CHANGE'; note?: string }
  ): Observable<void> {
    return from(
      supabase
        .rpc('admin_confirm_return_to_store', {
          p_order_id: Number(orderId),
          p_disposition: params.disposition,
          p_note: params.note || '',
        })
        .then(({ data, error }) => {
          if (error) throw error;
          const payload = data as Record<string, unknown> | null;
          if (payload && payload['success'] === false) {
            throw new Error(String(payload['error'] || 'Confirm return failed'));
          }
        })
    );
  }

  adminConfirmCodReceived(
    orderId: number | string,
    reason: string
  ): Observable<void> {
    return from(
      supabase
        .rpc('admin_confirm_cod_received', {
          p_order_id: Number(orderId),
          p_reason: reason,
        })
        .then(({ data, error }) => {
          if (error) throw error;
          const payload = data as Record<string, unknown> | null;
          if (payload && payload['success'] === false) {
            throw new Error(String(payload['error'] || 'Payment reconciliation failed'));
          }
        })
    );
  }

  assignRiderManually(params: {
    orderId: number | string;
    riderId: string;
    distanceKm?: number;
    estimatedMinutes?: number;
    note?: string;
  }): Observable<void> {
    return from(
      supabase
        .rpc('admin_assign_rider_to_order', {
          p_order_id: Number(params.orderId),
          p_rider_id: params.riderId,
          p_distance_km: params.distanceKm ?? 2,
          p_estimated_minutes: Math.max(
            1,
            Math.round(params.estimatedMinutes ?? 15)
          ),
          p_note: params.note ?? 'Rider assigned manually by admin',
        })
        .then(({ data, error }) => {
          if (error) throw error;

          const payload = data as Record<string, unknown> | null;
          if (payload && payload['success'] === false) {
            throw new Error(String(payload['error'] ?? 'Assignment failed'));
          }
        })
    );
  }

  createDelivery(params: {
    order_id: number;
    store_id: number;
    distance_km: number;
    estimated_time: number;
  }): Observable<void> {
    return from(
      supabase
        .rpc('assign_rider_to_order', {
          p_order_id: Number(params.order_id),
          p_store_id: Number(params.store_id),
          p_distance_km: params.distance_km,
          p_estimated_minutes: Math.round(params.estimated_time),
        })
        .then(({ data, error }) => {
          if (error) throw error;

          const responseText =
            typeof data === 'string'
              ? data
              : data?.message || data?.error || data?.status || '';

          if (
            String(responseText).toLowerCase().includes('no available rider')
          ) {
            throw new Error('No available rider found');
          }
        })
    );
  }

  getStoreAdminOrdersRPC(params: {
    target_store_id: string | null;
    filter_status?: string;
    filter_payment_status?: string;
    from_date?: string;
    to_date?: string;
    search?: string;
    limit_count?: number;
    offset_count?: number;
  }): Observable<AdminOrdersPage> {
    const rpcParams = {
      p_store_id: params.target_store_id
        ? Number(params.target_store_id)
        : null,
      p_status: params.filter_status ?? null,
      p_payment_status: params.filter_payment_status ?? null,
      p_from_date: params.from_date ?? null,
      p_to_date: params.to_date ?? null,
      p_search: params.search?.trim() || null,
      p_limit: params.limit_count ?? 25,
      p_offset: params.offset_count ?? 0,
    };

    return from(
      supabase
        .rpc('admin_list_orders_v2', rpcParams)
        .then(async ({ data, error }) => {
          if (error) throw error;

          const payload = (data || {}) as {
            data?: any[];
            total?: number;
            has_more?: boolean;
          };
          const rawOrders = Array.isArray(payload.data) ? payload.data : [];
          const orders = rawOrders.map((order) =>
            this.transformToProductionReady(order)
          );
          await this.enrichAdminOrderImages(orders);
          await this.enrichRiderPhotos(orders);
          orders.forEach((order) =>
            this.adminOrderCache.set(order.order_id, order)
          );
          return {
            data: orders,
            total: Number(payload.total || 0),
            hasMore: Boolean(payload.has_more),
          };
        })
    );
  }

  private transformToProductionReady(order: any): AdminOrder {
    const rawStatus =
      order.current_status || order.order_status || order.status || 'pending';
    const status = String(rawStatus).toUpperCase() as OrderStatus;
    const missingAddress = '\u274c Missing address';
    const missingPhone = '\u274c Missing phone';
    const addressSource =
      order.delivery?.address ||
      order.delivery_address ||
      order.customer?.delivery_address ||
      null;
    const cleanAddress = this.normalizeDeliveryAddress(addressSource);
    const cleanAddressStatus = cleanAddress ? 'Valid' : missingAddress;
    const cleanPhone =
      order.delivery_phone ||
      order.delivery?.phone ||
      order.customer?.phone ||
      addressSource?.phone_number ||
      addressSource?.phone ||
      missingPhone;
    const cleanPhoneStatus =
      cleanPhone === missingPhone ? 'Missing phone' : 'Valid';
    const cleanItems = this.normalizeOrderItems(order.items || []);
    const cleanPricing = this.normalizeOrderPricing(order, cleanItems);
    const calculatedItemCount = cleanItems.length;
    const calculatedQuantity = cleanItems.reduce(
      (sum, item) => sum + (item.ordered_quantity ?? item.order_quantity ?? 0),
      0
    );
    const cleanItemCount =
      calculatedItemCount ||
      Number(order.summary?.items ?? order.summary?.total_items ?? 0);
    const cleanQuantity =
      calculatedQuantity ||
      Number(order.summary?.quantity ?? order.summary?.item_count ?? 0);
    const cleanTotalAmount = Number(
      order.total_amount ??
        cleanPricing.subtotal ??
        cleanPricing.payable_amount ??
        order.payable_amount ??
        0
    );
    const cleanPayableAmount = Number(
      order.payable_amount ?? cleanPricing.payable_amount ?? cleanTotalAmount
    );
    const cleanPaymentStatus = String(order.payment_status || '').toUpperCase();
    const cleanStatusHistory = this.normalizeStatusHistory(
      order.status_history,
      status,
      order.created_at
    );
    const normalizedPromotions = (order.promotions || []).map((promotion: any) => ({
      promotion_id: promotion.promotion_id ?? promotion.promotion?.id ?? null,
      name: promotion.name ?? promotion.promotion?.name ?? '',
      type: promotion.type ?? promotion.promotion?.type ?? '',
      discount_amount: Number(promotion.discount_amount || 0),
    }));
    const deliveryAddressObj =
      order.delivery_address && typeof order.delivery_address === 'object'
        ? order.delivery_address
        : order.customer?.delivery_address &&
            typeof order.customer.delivery_address === 'object'
          ? order.customer.delivery_address
          : null;

    const deliveryCoords = {
      ...(order.delivery && typeof order.delivery === 'object'
        ? order.delivery
        : {}),
      address:
        order.delivery?.address ||
        deliveryAddressObj ||
        order.delivery_address,
      phone:
        order.delivery?.phone ||
        order.delivery_phone ||
        order.customer?.phone ||
        deliveryAddressObj?.phone_number ||
        deliveryAddressObj?.phone,
      lat: Number(
        order.delivery?.lat ??
          order.delivery_lat ??
          deliveryAddressObj?.lat ??
          deliveryAddressObj?.latitude ??
          0
      ),
      lng: Number(
        order.delivery?.lng ??
          order.delivery_lng ??
          deliveryAddressObj?.lng ??
          deliveryAddressObj?.longitude ??
          0
      ),
    };
    const normalizedStore = order.store
      ? {
          store_id: order.store.id ?? order.store.store_id ?? order.store_id,
          id: order.store.id ?? order.store.store_id ?? order.store_id,
          name: order.store.name ?? order.store.store_name ?? order.store_name,
          store_name:
            order.store.store_name ?? order.store.name ?? order.store_name,
          latitude: order.store.latitude,
          longitude: order.store.longitude,
          lat: order.store.latitude,
          lng: order.store.longitude,
        }
      : order.store_id
        ? {
            store_id: order.store_id,
            name: order.store_name,
            store_name: order.store_name,
          }
        : undefined;
    const rawRider = Array.isArray(order.rider)
      ? order.rider[0]
      : order.rider;
    const rawRiderAccount = Array.isArray(rawRider?.account)
      ? rawRider.account[0]
      : rawRider?.account;
    const rawRiderPresence = Array.isArray(rawRider?.presence)
      ? rawRider.presence[0]
      : rawRider?.presence;
    const rawProfilePhoto = Array.isArray(rawRider?.profile_photo)
      ? rawRider.profile_photo[0]
      : rawRider?.profile_photo;
    const normalizedRider: AdminOrderRider | null = rawRider
      ? {
          ...rawRider,
          rider_id: rawRider.id,
          name: rawRider.full_name || rawRiderAccount?.name || '',
          account: rawRiderAccount || null,
          presence: rawRiderPresence || null,
          profile_photo: rawProfilePhoto || null,
          profile_photo_url: null,
        }
      : null;
    const orderWithPricing = {
      ...order,
      pricing: order.pricing || {
        subtotal: order.total_amount,
        discount_amount: order.discount_amount,
        delivery_fee: order.delivery_fee,
        delivery_discount: order.delivery_discount,
        original_delivery_fee: order.original_delivery_fee,
        packaging_fee: order.packaging_fee,
        platform_fee: order.platform_fee,
        small_order_fee: order.small_order_fee,
        coin_used: order.coin_used,
        payable_amount: order.payable_amount,
      },
      promotions: normalizedPromotions,
    };

    return {
      ...orderWithPricing,
      order_id: order.order_id ?? order.id,
      order_number: order.order_number ?? null,
      status: status.toLowerCase(),
      current_status: status,
      payment_status: cleanPaymentStatus,
      payable_amount: cleanPayableAmount,
      total_amount: cleanTotalAmount,
      pricing: cleanPricing,
      created_at: order.created_at || '',
      updated_at: order.updated_at || order.created_at || '',
      delivery_address: cleanAddress || missingAddress,
      delivery_phone: cleanPhone,
      delivery: deliveryCoords,
      store: normalizedStore,
      promotions: normalizedPromotions,
      deliveries: order.deliveries || [],
      rider: normalizedRider,
      rider_id:
        order.rider_id ?? normalizedRider?.id ?? normalizedRider?.rider_id ?? null,
      picking_slip_print_triggered_at: order.picking_slip_print_triggered_at ?? null,
      picking_slip_trigger_count: Number(order.picking_slip_trigger_count || 0),
      picking_slip_claimed_at: order.picking_slip_claimed_at ?? null,
      fulfillment_revision: Number(order.fulfillment_revision || 0),
      picking_slip_printed_revision: order.picking_slip_printed_revision != null ? Number(order.picking_slip_printed_revision) : null,
      delivered_at: order.delivered_at ?? null,
      priority: this.getOrderPriority(status),
      action_required: this.getOrderAction(status),
      address_status: cleanAddressStatus,
      phone_status: cleanPhoneStatus,
      customer: {
        ...(order.customer || {}),
        user_id: order.customer?.user_id ?? order.customer?.id ?? order.user_id,
        receiver_name:
          order.customer?.receiver_name ||
          addressSource?.receiver_name ||
          order.customer?.name ||
          '',
        phone: cleanPhone,
        phone_status: cleanPhoneStatus,
        address_status: cleanAddressStatus,
        delivery_address:
          addressSource && typeof addressSource === 'object'
            ? addressSource
            : {},
      },
      summary: {
        items: cleanItemCount,
        quantity: cleanQuantity,
        total_amount: cleanPayableAmount,
        payment_status: cleanPaymentStatus,
        item_count: cleanItemCount,
        total_items: cleanQuantity,
      },
      items: cleanItems,
      timeline: cleanStatusHistory.map((history) => ({
        status: String(history.status || '').toLowerCase(),
        time: history.created_at || '',
      })),
      status_history: cleanStatusHistory,
    };
  }

  private getOrderPriority(status: OrderStatus): 'HIGH' | 'MEDIUM' | 'LOW' {
    if (status === 'PENDING' || status === 'CONFIRMED') return 'HIGH';
    if (status === 'PREPARING') return 'MEDIUM';
    return 'LOW';
  }

  private getOrderAction(status: OrderStatus): string {
    switch (status) {
      case 'PENDING':
        return 'Confirm order';
      case 'CONFIRMED':
        return 'Start packing';
      case 'PREPARING':
        return 'Mark ready & auto-assign rider';
      case 'READY_FOR_PICKUP':
        return 'Finding nearest rider';
      case 'RIDER_ASSIGNED':
        return 'Rider heading to store';
      case 'RIDER_ACCEPTED':
        return 'Rider accepted — en route to store';
      case 'RIDER_AT_STORE':
        return 'Rider at store — hand over order';
      case 'DISPATCHED':
        return 'In delivery';
      case 'RIDER_NEAR_CUSTOMER':
        return 'Rider nearby';
      case 'DELIVERED':
        return 'Completed';
      case 'CANCELLED':
      default:
        return 'No action';
    }
  }

  private normalizeDeliveryAddress(address: any): string {
    if (!address) return '';
    if (typeof address === 'string') return address.trim();

    if (address.formatted_address) return String(address.formatted_address);
    if (address.address) return String(address.address);

    return [
      address.house_number,
      address.building_name,
      address.street,
      address.area,
      address.city,
      address.state,
      address.pincode || address.zipcode,
    ]
      .filter(Boolean)
      .join(', ');
  }

  private normalizeOrderItems(items: any[]): AdminOrderItemViewModel[] {
    return items.map((item) => {
      const variant = item.variant || item.product_variant || {};
      const product = variant.product || item.product || {};
      
      const orderQuantity = Number(item.order_quantity ?? item.quantity ?? 0);
      const cancelledQuantity = Number(item.cancelled_quantity || 0);
      const fulfilledQuantity = Math.max(0, orderQuantity - cancelledQuantity);
      
      const variantQuantity = Number(
        item.variant_quantity ?? variant.quantity ?? 0
      );
      const price = Number(item.price || item.unit_price || 0);
      const originalTotal = Number(item.total ?? orderQuantity * price);
      
      const sku = String(item.sku || variant.sku || '');
      const unit = String(item.unit || variant.unit || '');
      const productName = String(item.product_name || product.name || '');
      const variantName = getVariantDisplayQuantity({
        net_quantity: item.net_quantity || variant.net_quantity,
        quantity: variantQuantity, unit,
      });
      
      const productId = this.getOrderItemProductId(item);

      const legacyImageUrl = this.getOrderItemImage(item) || null;

      return {
        id: Number(item.id || 0),
        
        sku,
        variant_sku: sku,
        product_name: productName,
        variant_name: variantName,
        image_url: legacyImageUrl,
        image: legacyImageUrl,
        unit,

        is_fully_removed: orderQuantity > 0 && fulfilledQuantity === 0,
        is_partially_removed: cancelledQuantity > 0 && fulfilledQuantity > 0,

        ordered_quantity: orderQuantity,
        removed_quantity: cancelledQuantity,
        fulfilled_quantity: fulfilledQuantity,

        historical_unit_price: price,
        original_line_total: originalTotal,
        removed_line_total: cancelledQuantity * price,
        fulfilled_line_total: fulfilledQuantity * price,
        
        // Legacy / Fallbacks bindings
        order_quantity: orderQuantity,
        cancelled_quantity: cancelledQuantity,
        product_variant_id: item.product_variant_id != null ? Number(item.product_variant_id) : undefined,
        price,
        mrp: Number(item.mrp || variant.mrp || 0),
        total: originalTotal,
        item_savings: Number(item.item_savings || 0),
        variant_quantity: variantQuantity,
      };
    });
  }

  private normalizeOrderPricing(
    order: any,
    items: AdminOrder['items']
  ): NonNullable<AdminOrder['pricing']> {
    const itemSubtotal = items.reduce(
      (sum, item) => sum + Number(item.total || 0),
      0
    );
    const pricing = order.pricing || {};
    const deliveryFee = Number(pricing.delivery_fee || 0);
    const packagingFee = Number(pricing.packaging_fee || 0);
    const platformFee = Number(pricing.platform_fee || 0);
    const smallOrderFee = Number(pricing.small_order_fee || 0);
    const discountAmount = Number(
      pricing.discount_amount ??
        (order.promotions || []).reduce(
          (sum: number, promotion: any) =>
            sum + Number(promotion.discount_amount || 0),
          0
        )
    );
    const coinUsed = Number(pricing.coin_used || 0);
    const subtotal = Number(
      pricing.subtotal ??
        order.total_amount ??
        itemSubtotal +
          deliveryFee +
          packagingFee +
          platformFee +
          smallOrderFee -
          discountAmount -
          coinUsed
    );

    return {
      subtotal,
      coin_used: coinUsed,
      delivery_fee: deliveryFee,
      platform_fee: platformFee,
      packaging_fee: packagingFee,
      payable_amount: Number(
        pricing.payable_amount ?? order.payable_amount ?? subtotal
      ),
      discount_amount: discountAmount,
      small_order_fee: smallOrderFee,
      delivery_discount: Number(pricing.delivery_discount || 0),
      original_delivery_fee: Number(
        pricing.original_delivery_fee ?? deliveryFee
      ),
    };
  }

  private getOrderItemProductId(item: any): number | null {
    const variant = item.variant || item.product_variant || {};
    const product = variant.product || item.product || {};
    const id =
      item.product_id ??
      product.id ??
      variant.product_id ??
      item.product?.id ??
      item.product_variant?.product_id;

    const numericId = Number(id);
    return Number.isFinite(numericId) && numericId > 0 ? numericId : null;
  }

  private getOrderItemImage(item: any): string {
    const variant = item.variant || item.product_variant || {};
    const product = variant.product || item.product || {};

    return (
      item.image ||
      item.image_url ||
      item.thumbnail_url ||
      product.image ||
      product.image_url ||
      product.thumbnail_url ||
      this.getPrimaryProductImageUrl(product.images) ||
      variant.image ||
      variant.image_url ||
      ''
    );
  }

  private getPrimaryProductImageUrl(images: any[] | undefined): string {
    if (!images?.length) return '';

    const image =
      images.find((img) => img.is_primary || img.isPrimary) || images[0];
    const file = image.files || image.file;

    if (image.url) return image.url;
    if (file?.bucket && file?.path) {
      return this.getPublicFileUrl(file.bucket, file.path);
    }

    return '';
  }

  private extractUniqueProductIds(orders: AdminOrder[]): number[] {
    const productIds = new Set<number>();
    orders.forEach((order) => {
      (order.items || []).forEach((item: any) => {
        if (!item.image && !item.image_url) {
          const productId = item.product_id || this.getOrderItemProductId(item);
          if (productId) {
            productIds.add(Number(productId));
          }
        }
      });
    });
    return Array.from(productIds);
  }

  private async enrichAdminOrderImages(orders: AdminOrder[]): Promise<void> {
    const orderIds = [...new Set(orders.map(order => Number(order.order_id)).filter(id => id > 0))];
    if (orderIds.length) {
      const { data: lines, error: lineError } = await supabase.from('order_items')
        .select('id, order_id, product_variant_id, variant:product_variants(id, product_id, sku, net_quantity, quantity, unit)')
        .in('order_id', orderIds);
      if (lineError) throw lineError;
      const resolvedLines = (lines || []).map(line => ({ ...line,
        variant: Array.isArray(line.variant) ? line.variant[0] : line.variant,
      }));
      for (const order of orders) {
        const orderLines = resolvedLines.filter(line => Number(line.order_id) === Number(order.order_id));
        for (const item of order.items || []) {
          const line = orderLines.find(line => Number(item.id) > 0
            ? Number(line.id) === Number(item.id)
            : Boolean(item.sku) && line.variant?.sku === item.sku);
          if (line?.variant) {
            item.variant_name = getVariantDisplayQuantity(line.variant);
            item.product_variant_id = Number(line.product_variant_id);
          }
        }
      }
    }
    const productIds = this.extractUniqueProductIds(orders);

    if (!productIds.length) return;

    const { data, error } = await supabase
      .from('product_images')
      .select(
        `
        product_id,
        is_primary,
        sort_order,
        files:files!product_images_file_id_fkey (
          bucket,
          path
        )
        `
      )
      .in('product_id', productIds)
      .order('is_primary', { ascending: false })
      .order('sort_order', { ascending: true });

    if (error) throw error;

    const imageByProductId = new Map<number, string>();
    (data ?? []).forEach((image: any) => {
      if (imageByProductId.has(image.product_id)) return;

      const url =
        image.files?.bucket && image.files?.path
          ? this.getPublicFileUrl(image.files.bucket, image.files.path)
          : '';

      if (url) imageByProductId.set(image.product_id, url);
    });

    orders.forEach((order) => {
      order.items = (order.items || []).map((item) => {
        const resolvedImageStr = item.image || item.image_url || (item as any).product_id ? imageByProductId.get((item as any).product_id) : '';
        const resolvedImageUrl = resolvedImageStr || null;
        
        return {
          ...item,
          image: resolvedImageUrl,
          image_url: resolvedImageUrl
        };
      });
    });
  }

  private getPublicFileUrl(bucket: string, path: string): string {
    return supabase.storage.from(bucket).getPublicUrl(path).data.publicUrl;
  }

  private normalizeStatusHistory(
    history: any[] | undefined,
    status: OrderStatus,
    createdAt: string
  ): AdminOrder['status_history'] {
    if (history?.length) {
      return history.map((entry) => ({
        status: String(entry.status || '').toLowerCase(),
        actor: entry.actor || 'System',
        note: entry.note || '',
        created_at: entry.created_at || entry.time || '',
      }));
    }

    return [
      {
        status: status.toLowerCase(),
        actor: 'System',
        note: 'Order received',
        created_at: createdAt || '',
      },
    ];
  }

  getStoreAdminOrderDetails(id: number): Observable<AdminOrder> {
    return from(this.fetchAdminOrderById(id));
  }

  lookupAdminOrders(query: string): Observable<AdminOrderLookupResult> {
    return from(this.lookupAdminOrdersInternal(query));
  }

  private async fetchAdminOrderById(id: number): Promise<AdminOrder> {
    const [{ data, error }, { data: canonicalData }] = await Promise.all([
      supabase.from(this.table).select(this.adminOrderDetailSelect).eq('id', id).single(),
      supabase.rpc('get_canonical_order_bill', { p_order_id: id })
    ]);

    if (error) throw error;

    const [order] = await this.mapAdminOrderRows([data]);
    if (!order) {
      throw new Error('Order not found');
    }

    if (canonicalData) {
      order.canonical_bill = canonicalData as unknown as CanonicalBillSummary;
    }

    return order;
  }

  private async lookupAdminOrdersInternal(
    query: string
  ): Promise<AdminOrderLookupResult> {
    const trimmed = query.trim();
    if (!trimmed) {
      return { orders: [], lookupType: 'empty' };
    }

    if (/^\d+$/.test(trimmed)) {
      const orderId = Number(trimmed);
      const { data, error } = await supabase
        .from(this.table)
        .select(this.adminOrderDetailSelect)
        .eq('id', orderId)
        .limit(1);

      if (error) throw error;

      const orders = await this.mapAdminOrderRows(data || []);
      if (orders.length) {
        return { orders, lookupType: 'order_id' };
      }
    }

    if (this.isUuid(trimmed)) {
      const { data: directRiderOrders, error: directRiderError } =
        await supabase
          .from(this.table)
          .select(this.adminOrderDetailSelect)
          .eq('rider_id', trimmed)
          .order('created_at', { ascending: false })
          .limit(50);

      if (directRiderError) throw directRiderError;

      const riderOrders = await this.mapAdminOrderRows(directRiderOrders || []);
      if (riderOrders.length) {
        return { orders: riderOrders, lookupType: 'rider_id' };
      }

      const { data: riderRecord } = await supabase
        .from('riders')
        .select('id')
        .eq('user_id', trimmed)
        .maybeSingle();

      if (riderRecord?.id) {
        const { data, error } = await supabase
          .from(this.table)
          .select(this.adminOrderDetailSelect)
          .eq('rider_id', riderRecord.id)
          .order('created_at', { ascending: false })
          .limit(50);

        if (error) throw error;

        const orders = await this.mapAdminOrderRows(data || []);
        if (orders.length) {
          return {
            orders,
            lookupType: 'rider_user_id',
            message: 'Matched rider account user ID',
          };
        }
      }

      const { data: customerOrders, error: customerError } = await supabase
        .from(this.table)
        .select(this.adminOrderDetailSelect)
        .eq('user_id', trimmed)
        .order('created_at', { ascending: false })
        .limit(50);

      if (customerError) throw customerError;

      const orders = await this.mapAdminOrderRows(customerOrders || []);
      if (orders.length) {
        return {
          orders,
          lookupType: 'customer_user_id',
          message: 'Matched customer user ID',
        };
      }
    }

    const { data: orderNumberMatches, error: orderNumberError } = await supabase
      .from(this.table)
      .select(this.adminOrderDetailSelect)
      .ilike('order_number', `%${trimmed}%`)
      .order('created_at', { ascending: false })
      .limit(20);

    if (orderNumberError) throw orderNumberError;

    const orders = await this.mapAdminOrderRows(orderNumberMatches || []);
    if (orders.length) {
      return { orders, lookupType: 'order_number' };
    }

    return {
      orders: [],
      lookupType: 'not_found',
      message: 'No orders found for this ID',
    };
  }

  private async mapAdminOrderRows(rawOrders: any[]): Promise<AdminOrder[]> {
    const orders = rawOrders.map((order) => this.transformToProductionReady(order));
    await this.enrichAdminOrderImages(orders);
    await this.enrichRiderPhotos(orders);
    orders.forEach((order) => this.adminOrderCache.set(order.order_id, order));
    return orders;
  }

  private async enrichRiderPhotos(orders: AdminOrder[]): Promise<void> {
    const urls = new Map<string, Promise<string | null>>();
    await Promise.all(orders.map(async order => {
      const rider = order.rider;
      const file = rider?.profile_photo;
      if (!rider || !file?.bucket || !file.path) return;
      const key = JSON.stringify([file.bucket, file.path]);
      if (!urls.has(key)) {
        urls.set(key, getStorageObjectUrl(file.bucket, file.path).catch(() => null));
      }
      rider.profile_photo_url = await urls.get(key) ?? null;
    }));
  }

  private isUuid(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      value
    );
  }

  previewOrderItemAdjustment(
    orderId: number | string,
    orderItemId: number | string,
    reduceBy: number
  ): Observable<any> {
    return from(
      supabase.rpc('preview_order_item_adjustment', {
        p_order_id: Number(orderId),
        p_order_item_id: Number(orderItemId),
        p_reduce_by: Number(reduceBy),
      }).then(({ data, error }) => {
        if (error) throw error;
        return data;
      })
    );
  }

  adminAdjustOrderItems(
    orderId: number | string,
    orderItemId: number | string,
    reduceBy: number,
    reason: string,
    note: string,
    disposition: string,
    idempotencyKey: string
  ): Observable<any> {
    return from(
      supabase.rpc('admin_adjust_order_items', {
        p_order_id: Number(orderId),
        p_order_item_id: Number(orderItemId),
        p_reduce_by: Number(reduceBy),
        p_reason: reason,
        p_note: note,
        p_disposition: disposition,
        p_idempotency_key: idempotencyKey,
      }).then(({ data, error }) => {
        if (error) throw error;
        const payload = data as Record<string, unknown> | null;
        if (payload && payload['success'] === false) {
            throw new Error(String(payload['error'] || 'Adjustment failed'));
        }
        return data;
      })
    );
  }
}
